/**
 * shapePoolKey — the DisplayPool key for a ShapeSpawner primitive (plan 16 §2.2).
 *
 * A key names everything a pooled graphics carries that cannot change without
 * a structural write or a redraw: its layer (glow or world), its depth, and the
 * exact draw calls (primitive geometry, colour after overrides, flipX). Tween,
 * offset, scatter, orbit and link fields only move the shape, so they are left
 * out. Re-acquiring a key therefore needs no draw call at all.
 *
 * Random geometry (jagged beams, jittered lines) is drawn from one of
 * `SHAPE_VARIANTS` seeded variants instead of fresh `Math.random()` per cast,
 * so the variant joins the key and a lightning bolt can be reused while still
 * looking different from cast to cast.
 */

import type { VFXPrimitive } from '../VFXEffectRegistry';

export const SHAPE_VARIANTS = 4;

/** Fields that move or schedule a shape but never change what it draws. */
const MOTION_FIELDS = new Set([
  'tween', 'offsetX', 'offsetY', 'scatter', 'orbit', 'link', 'depth', 'glow',
]);

const sigCache = new WeakMap<VFXPrimitive, string>();

function geometrySignature(prim: VFXPrimitive): string {
  let sig = sigCache.get(prim);
  if (sig === undefined) {
    sig = JSON.stringify(prim, (k, v) => (k !== '' && MOTION_FIELDS.has(k) ? undefined : v));
    sigCache.set(prim, sig);
  }
  return sig;
}

/** True when the primitive's geometry is randomised per cast. */
export function usesRandomGeometry(prim: VFXPrimitive): boolean {
  if (prim.type === 'beam') return (prim.beamSegments ?? 1) > 1;
  if (prim.type === 'line') return (prim.lineJitter ?? 0) > 0;
  return false;
}

export interface ShapeKey {
  key: string;
  /** Deterministic RNG for this variant's geometry (returns [0, 1)). */
  rng: () => number;
}

export function shapePoolKey(
  prim: VFXPrimitive,
  color: number,
  flipX: boolean,
  depth: number,
): ShapeKey {
  const sig = geometrySignature(prim);
  const variant = usesRandomGeometry(prim) ? Math.floor(Math.random() * SHAPE_VARIANTS) : 0;
  const key = `${prim.glow ? 'glow' : 'world'}|${depth}|${color}|${flipX ? 1 : 0}|${variant}|${sig}`;
  return { key, rng: seededRng(hashString(sig) ^ Math.imul(variant + 1, 0x9e3779b9)) };
}

function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** mulberry32 — small, fast, good enough for jitter. */
function seededRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
